<template>
  <div>
    <div class="row">
      <div class="col-12 col-sm-6 col-lg">
        <base-input v-model="value.streetNumber"
                    :validation="validation.streetNumber"
                    mask="XXXXXXXXXX"
                    maxlength="10"
                    :readonly="readonly"
                    :disabled="disabled"
                    label="Street Number">
        </base-input>
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-select v-model="value.streetDirection"
                     :options="streetDirections"
                     :readonly="readonly"
                     :validation="validation.streetDirection"
                     :disabled="disabled"
                     label="Street Direction">
        </base-select>
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-input v-model="value.streetName"
                    :readonly="readonly"
                    :validation="validation.streetName"
                    maxlength="50"
                    :disabled="disabled"
                    label="Street Name">
        </base-input>
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-select v-model="value.streetType"
                     :options="streetTypes"
                     :readonly="readonly"
                     :disabled="disabled"
                     :validation="validation.streetType"
                     label="Street Type">
        </base-select>
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-select v-model="value.sufDir"
                     :validation="validation.sufDir"
                     :options="streetDirections"
                     :readonly="readonly"
                     :disabled="disabled"
                     label="Suffix Direction">
        </base-select>
      </div>
      <div class="col-12 col-sm-6 col-lg-12">
        <base-input v-model="value.addressLine2"
                    :validation="validation.addressLine2"
                    :readonly="readonly"
                    maxlength="50"
                    :disabled="disabled"
                    label="Street Address Line 2">
        </base-input>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-lg">
        <base-select v-model="value.city"
                     :validation="validation.city"
                     :readonly="readonly"
                     v-if="chicagoLandCities"
                     :disabled="disabled"
                     :options="chicagoCityOptions"
                     label="City" />
        <base-input v-model="value.city"
                    :validation="validation.city"
                    maxlength="25"
                    :disabled="disabled"
                    :readonly="readonly"
                    v-else
                    label="City" />
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-select v-model="value.state"
                     :validation="validation.state"
                     label="State"
                     :disabled="disabled"
                     :readonly="readonly || restrictState"
                     :options="stateList">
        </base-select>
      </div>
      <div class="col-12 col-sm-6 col-lg">
        <base-input v-model="value.zipCode"
                    :validation="validation.zipCode"
                    label="Zip Code"
                    :readonly="readonly"
                    :disabled="disabled"
                    mask="#####">
        </base-input>
      </div>
    </div>
    <div class="row">
      <div class="col col-lg-4" v-if="showUnincorporated">
        <base-checkbox v-model="value.unincorporated"
                       :disabled="disabled"
                       label="Located in Unincorporated Cook County"
                       :readonly="readonly">
        </base-checkbox>
      </div>
      <div class="col=8" v-if="value.unincorporated" style="fo">
        <p style="font-size: 14px;">
          Since your project is located in unincorporated Cook County, you need to obtain two separate demolition permits.
          In addition to this permit you are requesting from the Cook County Department of Environment and Sustainability, you must also secure a demolition permit from the <a href="https://www.cookcountyil.gov/service/e-permits"> Cook County Department of Building and Zoning</a>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import BaseInput from './BaseInput';
import BaseSelect from './BaseSelect';
import YesNo from './YesNo';
import BaseCheckbox from './BaseCheckbox';

export default {
  name: 'v-address',
  components: {
    BaseInput, BaseSelect, YesNo, BaseCheckbox,
  },
  props: {
    value: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    restrictState: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    chicagoLandCities: {
      type: Boolean,
      default: false,
    },
    showUnincorporated: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object,
      default() {
        return {
          $error: false,
          $touch() {},
        };
      },
    },
  },
  mounted() {
    this.id = this._uid;
  },
  computed: {
    inputListeners() {
      const vm = this;
      return Object.assign({}, this.$listeners, {
        input(event) {
          vm.validation.$touch();
          vm.$emit('input', event.target.value);
        },
      });
    },
    chicagoCityOptions() {
      return this.chicagoCities.map(city => ({
        value: city,
        text: city || '',
      }));
    },
  },
  data() {
    return {
      id: null,
      streetDirections: [
        { value: null, text: '' },
        { value: 'N', text: 'North' },
        { value: 'E', text: 'East' },
        { value: 'S', text: 'South' },
        { value: 'W', text: 'West' },
        { value: 'NW', text: 'Northwest' },
        { value: 'NE', text: 'Northeast' },
        { value: 'SW', text: 'Southwest' },
        { value: 'SE', text: 'Southeast' },
      ],
      streetTypes: [
        { value: 'AVE', text: 'Avenue' },
        { value: 'BLVD', text: 'Boulevard' },
        { value: 'CRES', text: 'Crescent' },
        { value: 'CT', text: 'Court' },
        { value: 'DR', text: 'Drive' },
        { value: 'EXPY', text: 'Expressway' },
        { value: 'HWY', text: 'Highway' },
        { value: 'LN', text: 'Lane' },
        { value: 'PKWY', text: 'Parkway' },
        { value: 'PL', text: 'Place' },
        { value: 'PLZ', text: 'Plaza' },
        { value: 'RD', text: 'Road' },
        { value: 'ROW', text: 'Row' },
        { value: 'SQ', text: 'Square' },
        { value: 'ST', text: 'Street' },
        { value: 'TER', text: 'Terrace' },
        { value: 'TRL', text: 'Trail' },
        { value: 'WAY', text: 'Way' },
      ],
      stateList: [
        { value: 'AL', text: 'Alabama' },
        { value: 'AK', text: 'Alaska' },
        { value: 'AZ', text: 'Arizona' },
        { value: 'AR', text: 'Arkansas' },
        { value: 'CA', text: 'California' },
        { value: 'CO', text: 'Colorado' },
        { value: 'CT', text: 'Connecticut' },
        { value: 'DE', text: 'Delaware' },
        { value: 'DC', text: 'District Of Columbia' },
        { value: 'FL', text: 'Florida' },
        { value: 'GA', text: 'Georgia' },
        { value: 'HI', text: 'Hawaii' },
        { value: 'ID', text: 'Idaho' },
        { value: 'IL', text: 'Illinois' },
        { value: 'IN', text: 'Indiana' },
        { value: 'IA', text: 'Iowa' },
        { value: 'KS', text: 'Kansas' },
        { value: 'KY', text: 'Kentucky' },
        { value: 'LA', text: 'Louisiana' },
        { value: 'ME', text: 'Maine' },
        { value: 'MD', text: 'Maryland' },
        { value: 'MA', text: 'Massachusetts' },
        { value: 'MI', text: 'Michigan' },
        { value: 'MN', text: 'Minnesota' },
        { value: 'MS', text: 'Mississippi' },
        { value: 'MO', text: 'Missouri' },
        { value: 'MT', text: 'Montana' },
        { value: 'NE', text: 'Nebraska' },
        { value: 'NV', text: 'Nevada' },
        { value: 'NH', text: 'New Hampshire' },
        { value: 'NJ', text: 'New Jersey' },
        { value: 'NM', text: 'New Mexico' },
        { value: 'NY', text: 'New York' },
        { value: 'NC', text: 'North Carolina' },
        { value: 'ND', text: 'North Dakota' },
        { value: 'OH', text: 'Ohio' },
        { value: 'OK', text: 'Oklahoma' },
        { value: 'OR', text: 'Oregon' },
        { value: 'PA', text: 'Pennsylvania' },
        { value: 'RI', text: 'Rhode Island' },
        { value: 'SC', text: 'South Carolina' },
        { value: 'SD', text: 'South Dakota' },
        { value: 'TN', text: 'Tennessee' },
        { value: 'TX', text: 'Texas' },
        { value: 'UT', text: 'Utah' },
        { value: 'VT', text: 'Vermont' },
        { value: 'VA', text: 'Virginia' },
        { value: 'WA', text: 'Washington' },
        { value: 'WV', text: 'West Virginia' },
        { value: 'WI', text: 'Wisconsin' },
        { value: 'WY', text: 'Wyoming' },
      ],
      chicagoCities: [
        null, 'Alsip', 'Arlington Heights', 'Barrington', 'Barrington Hills', 'Bartlett', 'Bedford Park', 'Bellwood',
        'Bensenville', 'Berkeley', 'Berwyn', 'Blue Island', 'Bolingbrook', 'Bridgeview', 'Broadview', 'Brookfield',
        'Buffalo Grove', 'Burbank', 'Burnham', 'Burr Ridge', 'Calumet City', 'Calumet Park', 'Chicago (Unincorporated)',
        'Chicago Heights', 'Chicago Ridge', 'Cicero', 'Country Club Hills', 'Countryside', 'Crestwood', 'Deerfield',
        'Des Plaines', 'Dixmoor', 'Dolton', 'East Dundee', 'East Hazel Crest', 'Elgin', 'Elk Grove Village', 'Elmhurst',
        'Elmwood Park', 'Evanston', 'Evergreen Park', 'Flossmoor', 'Ford Heights', 'Forest Park', 'Forest View',
        'Franklin Park', 'Glencoe', 'Glenview', 'Glenwood', 'Golf', 'Hanover Park', 'Harvey', 'Harwood Heights',
        'Hazel Crest', 'Hickory Hills', 'Hillside', 'Hinsdale', 'Hodgkins', 'Hoffman Estates', 'Hometown', 'Homewood',
        'Indian Head Park', 'Inverness', 'Justice', 'Kenilworth', 'La Grange', 'LaGrange Highlands', 'La Grange Park',
        'Lansing', 'Lemont', 'Lincolnwood', 'Lynwood', 'Lyons', 'Markham', 'Matteson', 'Maywood', 'McCook',
        'Melrose Park', 'Merrionette Park', 'Midlothian', 'Morton Grove', 'Mount Prospect', 'Niles', 'Norridge',
        'North Riverside', 'Northbrook', 'Northfield', 'Northlake', 'Norwood Park', 'Oak Brook', 'Oak Forest',
        'Oak Lawn', 'Oak Park', 'Olympia Fields', 'Orland Hills', 'Orland Park', 'Palatine', 'Palos Heights',
        'Palos Hills', 'Palos Park', 'Park Forest', 'Park Ridge', 'Phoenix', 'Posen', 'Prospect Heights',
        'Richton Park', 'River Forest', 'River Grove', 'Riverdale', 'Riverside', 'Robbins', 'Rolling Meadows',
        'Roselle', 'Rosemont', 'Sauk Village', 'Schaumburg', 'Schiller Park', 'Skokie', 'South Barrington',
        'South Chicago Heights', 'South Holland', 'Steger', 'Stickney', 'Stone Park', 'Streamwood', 'Summit',
        'Thornton', 'Tinley Park', 'University Park', 'Westchester', 'Western Springs', 'Wheeling', 'Willow Springs',
        'Wilmette', 'Winnetka', 'Worth',
      ],
    };
  },
};
</script>
